"use client";

import * as React from "react";
import { LiquidPill } from "components/ui/v2";
import { FileQuestion, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface PageEmptyStateV2Props {
  variant?: "empty" | "noResult";
  icon?: React.ReactNode;
  title: string;
  description?: string;
  onClearSearch?: () => void;
  clearSearchLabel?: string;
  actions?: React.ReactNode;
  className?: never;
  minHeightClassName?: string;
  /** Fill modal/side-panel body height (§5.5). */
  embedded?: boolean;
}

export function PageEmptyStateV2({
  variant = "empty",
  icon,
  title,
  description,
  onClearSearch,
  clearSearchLabel,
  actions,
  minHeightClassName = "min-h-[360px]",
  embedded = false,
}: PageEmptyStateV2Props) {
  const showClearSearch = variant === "noResult" && onClearSearch && clearSearchLabel;

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center text-center",
        embedded ? "min-h-[220px] flex-1" : minHeightClassName,
      )}
    >
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-slate-200/80 bg-white/75 text-slate-500 shadow-sm backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/80 dark:text-zinc-300">
        {icon ?? <FileQuestion className="h-7 w-7" />}
      </div>
      <h3
        className={cn(
          "font-semibold text-slate-950 dark:text-white",
          embedded ? "text-[13px] leading-snug" : "text-lg"
        )}
      >
        {title}
      </h3>
      {description ? (
        <p
          className={cn(
            "mt-2 max-w-md leading-6 text-slate-600 dark:text-zinc-400",
            embedded ? "text-[9px] leading-snug" : "text-sm"
          )}
        >
          {description}
        </p>
      ) : null}
      {(showClearSearch || actions) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {showClearSearch ? (
            <LiquidPill
              as="button"
              type="button"
              variant="slate"
              size="md"
              icon={<X className="h-3.5 w-3.5" />}
              text={clearSearchLabel}
              onClick={onClearSearch}
            />
          ) : null}
          {actions}
        </div>
      )}
    </div>
  );
}
